import * as React from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Root from '@components/root/Root'
import Modal from '@components/modal/Modal'
import ModalLoader from '@components/modal/ModalLoader'
import Button from '@components/button/Button'
import Icon from '@components/icon/Icons'
import Drawer from '@components/drawer/Drawer'
import SimpleAlert from '@components/modal/SimpleAlert'
import Redirect from '@components/view/Redirect'

import Layout from './IncidentLayout'
import ActivityLayout from './layouts/ActivityLayout'
import Start from './forms/Start'
import Delete from './forms/Delete'
import Chapter from './layouts/Chapter'

import useI18n from '@i18n/useI18n'
import useSyncStore from '@store/useSyncStore'
import useReducer from '@store/useReducer'
import * as IncidentStore from '@store/incident/incident'
import * as SiteStore from '@store/site/site'
import * as FamilleStore from '@store/famille/famille'
import * as LocationStore from '@store/location/location'
import * as SocieteStore from '@store/societe/societe'
import * as AuthStore from '@store/auth/auth'
import * as PlanStore from '@store/plan/plan'
import * as EDLStore from '@store/edl/edl'
import * as CommandeStore from '@store/commande/commande'
import * as UserStore from '@store/user/user'

import { SCREENS } from './workflow'
import useWorkflow from './useWorkflow'

import api from '@api/api'

import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'

import { orderManifestations } from '@screens/manifestations/manifestationsUtils'
import { canUserCreateIncident } from './utils'
import logger from '@utils/logger'

import * as uuid from '@utils/uuid'
import Logger from '@utils/logger'

import { AuthScope, EtatDesLieuxTypeEnum } from '@api/enum'

interface Props {
  formStep?: IncidentStep
  manifestationId?: string
  edlId?: string
  spaceId?: string
  mode?: 'edit' // edition mode
  copy?: boolean
  x?: string
  y?: string
}

const OPTIONS_MAPPING = {
  withManifestationId: 'manifestationId',
  withEDLId: 'edlId',
  withSpaceId: 'spaceId',
  mode: 'mode',
  positionX: 'x',
  positionY: 'y',
} as const

const IncidentFormScreen = ({ formStep, manifestationId, edlId, spaceId, mode, copy, x, y }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const [, bp] = useBreakpoint()

  const edlForm = useReducer(EDLStore.store, (s) => s.form)
  const form = useReducer(IncidentStore.store, (s) => s.form)
  const forms = useReducer(IncidentStore.store, (s) => s.forms)
  const scopes = useReducer(AuthStore.store, (s) => s.scopes)

  const sites = useReducer(SiteStore.store, (s) => s.sites)
  const site = useReducer(SiteStore.store, (s) => s.site)
  const user = useReducer(UserStore.store, (s) => s.user)

  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations.inProgress)
  const manifestation = useReducer(SiteStore.store, (s) => s.manifestation)

  const canCreateIncident = canUserCreateIncident(
    user,
    manifestation && manifestations.find((m) => m.id === manifestation.id)
  )

  // unique id, more stable than date to prevent duplicate
  const [id, setId] = React.useState(uuid.createUUID())

  const [step, setStep] = React.useState(0)
  const [loading, setLoading] = React.useState(false)
  const [fetchingCommands, setFetchingCommands] = React.useState(false)

  // navigate to next require system
  const [navigateRequired, setNavigateRequired] = React.useState(false)

  const [, , actions] = useSyncStore()

  const options = React.useMemo(
    () => ({
      withManifestationId: manifestationId,
      withEDLId: edlId,
      withSpaceId: spaceId,
      mode,
      positionX: x,
      positionY: y,
    }),
    [manifestationId, edlId, spaceId, mode, x, y]
  )

  const navOptions = React.useMemo(() => {
    const opts = Object.keys(options).filter((key) => options[key as keyof typeof options])

    return opts.length > 0
      ? `?${opts
          .map((o) => `${OPTIONS_MAPPING[o as keyof typeof options]}=${options[o as keyof typeof options]}`)
          .join('&')}`
      : ''
  }, [options])

  const [workflow, starter] = useWorkflow(form, options)

  const previous = React.useMemo(
    () =>
      forms
        .filter((f) => !!site && f.siteId === site.id)
        .sort((a, b) => b.creationDate!.localeCompare(a.creationDate!)),
    [forms, site]
  )

  const NO_HEADER = bp === 'phone' && workflow[step] === 'RECAP'
  const NO_START_LOADING = !!edlId && workflow[step] === 'START'

  React.useEffect(() => {
    // save form in drafts when change (only if not from edl)
    if (form && form.id && !form.edlId && !!user) {
      const next = [...forms.filter((f) => f.id !== form.id), form]
      IncidentStore.actions.setForms(next)
    }
  }, [form])

  React.useEffect(() => {
    if (site) {
      getSiteData(site.id)
    } else {
      getSites()
    }
  }, [])

  React.useEffect(() => {
    if (edlId) {
      // reset any data when in edl
      IncidentStore.actions.resetForm()
      IncidentStore.actions.setForm({ remonteDansEtatDesLieux: true })
    }

    if (form.id && form.step === 'START') {
      // reset any data when form already exist
      IncidentStore.actions.resetForm()
    }

    IncidentStore.actions.setForm(starter)
  }, [starter, edlId])

  React.useEffect(() => {
    const idx = workflow.findIndex((s) => s.toLowerCase() === formStep)

    if (idx < 0) {
      const next = workflow[0]
      navigation.push(`/incident/${next.toLowerCase()}${navOptions}`)
    } else {
      setStep(idx)
    }
  }, [workflow, formStep])

  React.useEffect(() => {
    // register last screen visited for every form
    if (step > 0) {
      IncidentStore.actions.setForm({ step: workflow[step] })
    }
  }, [step])

  React.useEffect(() => {
    if (sites.length === 1 && !user) {
      // if only 1 site available, no selection, require to update data for this site
      getSiteData(sites[0].id)
    }
  }, [sites, user])

  React.useEffect(() => {
    if (navigateRequired) {
      // on navigation asked, go to next
      // action divided to get an updated workflow before navigate
      setNavigateRequired(false)
      onNext()
    }
  }, [navigateRequired])

  React.useEffect(() => {
    if (form.siteId && form.manifestationId) {
      if (user) {
        setFetchingCommands(true)
        api.commande
          .all(form.siteId, form.manifestationId)
          .then((list) => CommandeStore.actions.setCommandes(list.commandes))
          .catch(Logger.error)
          .finally(() => setFetchingCommands(false))
      }
    }
  }, [form.siteId, form.manifestationId])

  const getSites = () => {
    ModalLoader.open({ title: i18n.t('screens.incident.SITE.loading') })

    api.site
      .all()
      .then((res) => SiteStore.actions.setSites(res))
      .finally(ModalLoader.close)
  }

  // console.log('F>ORM', form)

  const getSiteData = (siteId: string) => {
    if (edlId) {
      // skip initialize for edl
      return Promise.resolve()
    }

    if (!user) {
      // no previous data for not connected user -> add loading
      setLoading(true)
    }

    // fetch datas to complete form
    return Promise.all([
      api.localisation.all(siteId, true),
      api.familles.all(siteId, true),
      api.societe.all(siteId),
      api.manifestation.all(siteId),
      scopes.includes(AuthScope.DemandesPlanLecture) ? api.plan.all(siteId) : Promise.resolve([]),
    ])
      .then(([pl, ac, se, ma, pla]) => {
        LocationStore.actions.setPlaces(pl)
        FamilleStore.actions.setActivities(ac)
        SocieteStore.actions.setServices(se)
        SiteStore.actions.setManifestations(orderManifestations(ma))
        PlanStore.actions.setPlans(pla)
      })
      .catch(() => logger.error('data fetch failed'))
      .finally(() => setLoading(false))
  }

  const onForm = (item: Partial<IncidentForm>, withNavigation = false) => {
    const idAndDate = !form.id ? { id, creationDate: new Date().toISOString() } : {}

    IncidentStore.actions.setForm({ ...idAndDate, ...item })

    if (withNavigation) {
      setTimeout(() => setNavigateRequired(true), 200)
    }

    return Promise.resolve()
  }

  const onStart = (item?: Partial<IncidentForm>) => {
    IncidentStore.actions.resetForm()

    if (item) {
      // already started, we set current
      IncidentStore.actions.setForm(item)
    } else {
      // reload unique id generation
      setId(uuid.createUUID())

      // new form
      IncidentStore.actions.setForm({ ...starter })
    }

    // return to last visited step, or go to first step
    if (item && item.step) {
      navigation.push(`/incident/${item.step.toLowerCase()}${navOptions}`)
    } else {
      onNext()
    }
  }

  const onSiteSelected = () => {
    const s = sites.find((s) => s.id === form.siteId)

    if (s) {
      getSiteData(s.id).then(() => {
        const next = workflow[step + 1] || ''
        navigation.push(`/incident/${next.toLowerCase()}${navOptions}`)
      })
    }
  }

  const onStep = (next: IncidentStepScreen) => {
    navigation.push(`/incident/${next.toLowerCase()}${navOptions}`)
  }

  const onNext = () => {
    const next = workflow[step + 1] || ''
    navigation.push(`/incident/${next.toLowerCase()}${navOptions}`)
  }

  const onBack = () => {
    const previous = workflow[step - 1] || ''
    navigation.push(`/incident/${previous.toLowerCase()}${navOptions}`)
  }

  const onDelete = (id: string) => {
    IncidentStore.actions.removeForm(id)
    IncidentStore.actions.resetForm()
    Drawer.close()
  }

  const quit = () => {
    if (!!x || !!y) {
      navigation.push('/plan')
    } else if (edlId) {
      // back to map
      navigation.jumpTo(`/edl/map/${form.edlId}?spaceId=${form.space}`)
    } else if (step > 0 && workflow.includes('START')) {
      navigation.push(`/incident/start`)
    } else {
      navigation.reset()
    }
  }

  const onChapter = () => {
    Drawer.open({
      render: () => (
        <Chapter
          items={workflow}
          onItemSelected={(next) => {
            onStep(next)
            Drawer.close()
          }}
          onQuit={() => {
            quit()
            Drawer.close()
          }}
          selected={workflow[step]}
        />
      ),
    })
  }

  const onFinish = () => {
    if (form.edlId && edlForm.incidentIds) {
      const inc = actions.addIncident(form)
      const update: Partial<EDLForm> = {
        incidentAcceptIds: [...edlForm.incidentAcceptIds, inc.id],
        incidentOrganisateurIds:
          edlForm.type === EtatDesLieuxTypeEnum.SORTIE
            ? [...edlForm.incidentOrganisateurIds, inc.id]
            : edlForm.incidentOrganisateurIds,
        incidentIds: {
          token: edlForm.incidentIds.token,
          incidentIds: [...edlForm.incidentIds.incidentIds, inc.id],
        },
      }

      EDLStore.actions.setForm(update)
      IncidentStore.actions.removeForm(form.id)
      IncidentStore.actions.resetForm()
      navigation.jumpTo(`/edl/map/${form.edlId}?spaceId=${form.space}`)
      // Modal.open({ Content: renderFinish, cancel: false })
    } else {
      ModalLoader.open({ title: i18n.t('screens.incident.sending') })

      actions
        .upload(form, finish)
        .then(() => {
          ModalLoader.close()
          Modal.open({ Content: renderFinish, cancel: false })
        })
        .catch(() => {
          ModalLoader.close()

          SimpleAlert.open({
            title: i18n.t('common.error'),
            description: i18n.t('screens.incident.sendingFailed'),
            buttons: [{ label: i18n.t('common.ok'), onClick: () => SimpleAlert.close() }],
          })
        })
    }
  }

  const deleteIncident = () => {
    Drawer.open({
      render: () => <Delete data={previous} onClose={Drawer.close} onDelete={onDelete} />,
      hideTarget: true,
    })
  }

  const reset = () => {
    Modal.close()
    quit()

    IncidentStore.actions.removeForm(form.id)
    IncidentStore.actions.resetForm()
  }

  const finish = () => {
    Modal.close()
    navigation.reset()

    IncidentStore.actions.removeForm(form.id)
    IncidentStore.actions.resetForm()
  }

  const renderFinish = () => {
    return (
      <FinishContainer>
        <Title>{i18n.t('screens.incident.sent')}</Title>
        <Info>{i18n.t('screens.incident.sentInfo')}</Info>
        <Buttons>
          <ButtonContainer>
            <Button label={i18n.t('screens.incident.new')} onClick={reset} color="light" status="active" />
          </ButtonContainer>
          <ButtonContainer>
            <Button label={i18n.t('common.finish')} onClick={finish} status="active" />
          </ButtonContainer>
        </Buttons>

        <Close activeOpacity={0.8} onPress={finish}>
          <Icon name="close" color={theme.colors.primary} size={25} />
        </Close>
      </FinishContainer>
    )
  }

  const renderStep = (screen: IncidentStepScreen) => {
    switch (screen) {
      case 'START':
        return (
          <ItemContainer key={screen}>
            <Start onItemSelected={onStart} onQuit={quit} />
          </ItemContainer>
        )
      case 'TYPE':
      case 'LOCATION':
      case 'DETAIL':
      case 'PHOTO':
      case 'PRESTA':
      case 'MANIF':
      case 'MANIF_EXPO':
      case 'USER':
      case 'USER_DETAIL':
        const FormScreen = SCREENS[screen]

        return (
          <ItemContainer key={screen}>
            <Layout
              loading={loading}
              steps={workflow}
              step={screen}
              onStep={onStep}
              form={form}
              onBack={screen === 'MANIF' && (!!x || !!y) ? () => navigation.push('/plan') : onBack}
              onNext={onNext}
              onQuit={quit}>
              <FormScreen form={form} onForm={onForm} onBack={onBack} spaceId={spaceId} />
            </Layout>
          </ItemContainer>
        )

      case 'COMMAND_SEARCH':
      case 'COMMAND_SEARCH_EXPO':
      case 'POSITION':
        const FixedFormScreen = SCREENS[screen]

        return (
          <ItemContainer key={screen}>
            <Layout
              loading={loading || fetchingCommands}
              steps={workflow}
              step={screen}
              onStep={onStep}
              form={form}
              onBack={onBack}
              onNext={onNext}
              onQuit={quit}
              fixed>
              <FixedFormScreen
                fetching={fetchingCommands}
                form={form}
                onForm={onForm}
                onBack={onBack}
                onNext={onNext}
              />
            </Layout>
          </ItemContainer>
        )

      case 'RECAP':
        const Recap = SCREENS[screen]

        return (
          <ItemContainer key={screen}>
            <Layout
              loading={loading}
              steps={workflow}
              step={screen}
              onStep={onStep}
              form={form}
              onBack={copy ? navigation.goBack : onBack}
              onNext={onFinish}
              onQuit={quit}
              last
              noHeader={bp === 'phone'}>
              <Recap form={form} onForm={onForm} />
            </Layout>
          </ItemContainer>
        )
      case 'COMMAND':
      case 'COMMAND_EXPO':
        const Command = SCREENS[screen]

        return (
          <ItemContainer key={screen}>
            <Layout
              loading={loading}
              noHeader={bp === 'phone'}
              steps={workflow}
              step={screen}
              onStep={onStep}
              form={form}
              onBack={onBack}
              onNext={onNext}
              onQuit={quit}
              full
              fixed>
              <Command form={form} onForm={onForm} />
            </Layout>
          </ItemContainer>
        )
      case 'SITE':
      case 'SITE_EXPO':
        const Site = SCREENS[screen]

        return (
          <ItemContainer key={screen}>
            <Layout
              loading={loading}
              steps={workflow}
              step={screen}
              onStep={onStep}
              form={form}
              onBack={onBack}
              onNext={onSiteSelected}
              onQuit={quit}>
              <Site form={form} onForm={onForm} onBack={onBack} />
            </Layout>
          </ItemContainer>
        )
      case 'ACTIVITY':
        return (
          <ItemContainer key={screen}>
            <ActivityLayout
              form={form}
              onBack={onBack}
              onNext={onNext}
              onForm={onForm}
              onQuit={quit}
              edit={mode === 'edit'}
              steps={workflow}
              onStep={onStep}
            />
          </ItemContainer>
        )
      case 'MAP':
        const Map = SCREENS[screen]
        return (
          <ItemContainer key={screen}>
            <Layout
              loading={loading}
              steps={workflow}
              step={screen}
              onStep={onStep}
              form={form}
              onBack={onBack}
              onNext={onNext}
              onQuit={quit}>
              <Map form={form} onForm={onForm} onBack={onBack} />
            </Layout>
          </ItemContainer>
        )
      default:
        return null
    }
  }

  if (!canCreateIncident) {
    return <Redirect />
  }

  if (NO_HEADER) {
    return <Root fixed>{renderStep(workflow[step])}</Root>
  }

  return (
    <Root
      left={step === 0 ? 'close' : 'menu'}
      onLeft={step === 0 ? quit : onChapter}
      right={step === 0 ? 'delete' : undefined}
      onRight={step === 0 ? deleteIncident : undefined}
      header={i18n.t('screens.incidentForm.title')}
      loading={NO_START_LOADING}
      fixed>
      {renderStep(workflow[step])}
    </Root>
  )
}

export default IncidentFormScreen

const ItemContainer = styled(View)`
  flex: 1;
`

const FinishContainer = styled(View)`
  position: relative;
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? '24px 16px;' : '50px 140px')};
  max-width: 880px;
  background-color: ${(props) => props.theme.colors.background};
`

const Close = styled(TouchableOpacity)`
  position: absolute;
  top: ${(props) => (props.theme.breakpoint === 'phone' ? '0px' : '15px')};
  right: ${(props) => (props.theme.breakpoint === 'phone' ? '0px' : '15px')};
  padding: 15px;
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Title1};
  font-size: 24px;
  margin-bottom: 24px;
  text-align: center;
`

const Info = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  text-align: center;
`

const Buttons = styled(View)`
  flex-direction: ${(props) => (props.theme.breakpoint === 'phone' ? 'column' : 'row')};
  margin-top: 38px;
`

const ButtonContainer = styled(View)`
  ${(props) => props.theme.breakpoint !== 'phone' && `width: 270px;`}
  margin: 10px;
`
